import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/material/Grid'
import PropTypes from 'prop-types'

import {
    orderAttachmentsPath,
    useDeleteOrderAttachmentMutation,
    useGetOrderAttachmentsQuery,
} from '@/app/services/orderAttachments'
import { uploadAttachmentFile } from '@/app/services/uploadAttachmentFile'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags'
import { useSignIn } from '@/common/hooks/useSignIn'

import AttachmentsPanel from './AttachmentsPanel'
import { freshDownloadUri } from './freshDownloadUri'

// No polling: an upload waits for its scan verdict, so a row is usually settled once the list
// refreshes. Only a late verdict (504) leaves it pending, and the next page load resolves it. If
// that turns out to be common, add a backend list endpoint to poll rather than re-fetching the quote.

const OrderAttachmentsPanel = () => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const { isAuthenticated } = useKindeAuth()
    const signIn = useSignIn()
    const organisationId = useSelector(selectOrganisationId)

    const {
        data: attachments = [],
        fulfilledTimeStamp,
        isLoading,
        refetch,
    } = useGetOrderAttachmentsQuery({ organisationId, orderId })

    const [deleteAttachment] = useDeleteOrderAttachmentMutation()

    const resolveDownloadUri = (attachment) =>
        freshDownloadUri({
            attachment,
            fetchedAt: fulfilledTimeStamp,
            refetchRows: async () => {
                const { data, error } = await refetch()
                if (error) throw error
                return data
            },
        })

    return (
        <AttachmentsPanel
            attachments={attachments}
            canUpload={Boolean(isAuthenticated)}
            isLoading={isLoading}
            resolveDownloadUri={resolveDownloadUri}
            title={t('Quote attachments')}
            onDelete={(documentId) => deleteAttachment({ organisationId, orderId, documentId }).unwrap()}
            onSignIn={signIn}
            onUploadFile={(file, options) =>
                uploadAttachmentFile({ path: orderAttachmentsPath({ organisationId, orderId }), file, ...options })
            }
            onUploadsSettled={refetch}
        />
    )
}

const card = {
    backgroundColor: 'background.paper',
    borderRadius: 2,
    border: `1px solid ${blueGrey[200]}`,
    width: '100%',
    padding: 3,
}

/**
 * OrderAttachments - Documents on a web store quote, behind the `showQuoteAttachments` PostHog
 * flag. With the flag off nothing mounts: no card, no sign-in prompt, no requests. The flag
 * hides the feature only; the server's IsPublic filter is the security boundary.
 *
 * Renders its own full-width Grid card so a page never shows an empty wrapper with the flag off.
 * Reads the quote id from the route and the organisation from the store, like the other order
 * page sections.
 *
 * @param {Object} props
 * @param {Object} [props.sx] - Extra styles for the card, e.g. a top margin outside a spaced Grid
 */
const OrderAttachments = ({ sx }) => {
    const { showQuoteAttachments } = useFeatureFlags()
    if (!showQuoteAttachments) return null
    return (
        <Grid
            size={12}
            sx={[card, sx]}
        >
            <OrderAttachmentsPanel />
        </Grid>
    )
}

OrderAttachments.propTypes = {
    sx: PropTypes.object,
}

export default OrderAttachments
